import { useRef, useState } from 'react';
import { errorMessage } from '@/lib/api';
import {
  MAX_IMAGE_BYTES,
  removeBuildingImage,
  uploadBuildingImage,
} from '@/services/catalogImageService';

interface BuildingPhotoProps {
  name: string;
  url?: string;
  onUploaded: (images: Record<string, string>) => void;
  onError: (message: string) => void;
}

/**
 * One photo per building, named rather than listed — this is what the public
 * site uses as its hero image, so there is exactly one of it and replacing it
 * is the normal operation.
 */
export default function BuildingPhoto({
  name,
  url,
  onUploaded,
  onError,
}: BuildingPhotoProps) {
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const run = async (work: () => Promise<{ buildingImages: Record<string, string> }>) => {
    if (busy) return;
    setBusy(true);
    try {
      onUploaded((await work()).buildingImages);
    } catch (error) {
      onError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      onError(`${file.name} is larger than 10 MB.`);
      return;
    }
    void run(() => uploadBuildingImage(name, file));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-500 uppercase">Photo</span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => input.current?.click()}
            disabled={busy}
            className="text-xs font-semibold text-babcock-navy-700 hover:underline disabled:opacity-50 cursor-pointer"
          >
            <i className={busy ? 'ri-loader-4-line animate-spin mr-1' : 'ri-upload-2-line mr-1'}></i>
            {url ? 'Replace' : 'Add photo'}
          </button>
          {url && (
            <button
              type="button"
              onClick={() => void run(() => removeBuildingImage(name))}
              disabled={busy}
              className="text-xs font-semibold text-red-600 hover:underline disabled:opacity-50 cursor-pointer"
            >
              Remove
            </button>
          )}
        </div>
        <input
          ref={input}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFile}
          className="hidden"
        />
      </div>

      {url ? (
        <img
          src={url}
          alt={`${name} building`}
          loading="lazy"
          className="w-full h-28 object-cover rounded-md border border-gray-200"
        />
      ) : (
        <p className="text-xs text-gray-400">
          No photo yet. The public site falls back to the property logo.
        </p>
      )}
    </div>
  );
}
